import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LeftOutlined,
  CheckCircleFilled,
  DeleteOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Button, Modal, Spin, message } from 'antd';
import {
  dissolveOrganizationStream,
  getOrganizationDetail,
  type OrganizationItem,
} from '../../api';
import { useConfig } from '../../hooks/useConfig';
import { usePageEventQueue } from '../scenarioTask/hooks/usePageEventQueue';
import OrganizationDiagramRenderer from '../scenarioTask/components/runningProcess/OrganizationDiagramRenderer';
import type { OrganizationRuntimeState } from '../scenarioTask/components/runningProcess/types';
import type { OrganizationGraphCompileResult } from '../scenarioTask/components/runningProcess/utils/organizationGraph';
import {
  applyDissolveStreamItemToStates,
  buildFullyLitOrgStates,
} from './utils/diagramRuntime';
import {
  parseOrganizationStreamChunk,
  type OrganizationStreamItem,
} from './utils/stream';

const OrganizationDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [messageApi, contextHolder] = message.useMessage();
  const { globalConfig } = useConfig();
  const {
    createQueue,
    enqueue,
    destroy: destroyQueue,
  } = usePageEventQueue<OrganizationStreamItem>();

  const [dissolving, setDissolving] = useState(false);
  const [dissolveFinished, setDissolveFinished] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [orgStates, setOrgStates] = useState<
    Map<string, OrganizationRuntimeState>
  >(() => new Map());

  const abortControllerRef = useRef<AbortController | null>(null);
  const completeTimerRef = useRef<number | null>(null);
  const graphRef = useRef<OrganizationGraphCompileResult | null>(null);

  const pageConsumeIntervalMs =
    globalConfig.demoMode === true
      ? (globalConfig.demoPageConsumeInterval ?? 0)
      : 0;

  const orgInfo = useMemo<OrganizationItem | null>(() => {
    if (!id) return null;
    const raw = sessionStorage.getItem(`org_${id}`);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [id]);

  const {
    data: detail,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['organization-detail', id],
    queryFn: () => getOrganizationDetail(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (!(error instanceof Error)) return;
    messageApi.error(error.message);
  }, [error, messageApi]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      destroyQueue();
      if (completeTimerRef.current !== null) {
        window.clearTimeout(completeTimerRef.current);
      }
    };
  }, [destroyQueue]);

  /** 图编译完成后，如果当前不是解散中，则直接把整张组织图点亮。 */
  const handleCompiled = useCallback(
    (graph: OrganizationGraphCompileResult) => {
      graphRef.current = graph;
      // 解散完成后的 3 秒停留期必须保留“全暗”最终态，不能因为图重新编译又回填全亮。
      if (!dissolving && !dissolveFinished) {
        setOrgStates(buildFullyLitOrgStates(graph));
      }
    },
    [dissolveFinished, dissolving]
  );

  useEffect(() => {
    // 常规详情态保持整图点亮；进入解散中或已解散完成停留态后，不再自动回填全亮。
    if (!detail || !graphRef.current || dissolving || dissolveFinished) return;
    setOrgStates(buildFullyLitOrgStates(graphRef.current));
  }, [detail, dissolveFinished, dissolving]);

  /** 页面队列真正消费解散事件时，再让对应节点逐步点暗。 */
  const handleQueueItem = useCallback(
    (item: OrganizationStreamItem) => {
      const graph = graphRef.current;

      if (item.type === 'error') {
        messageApi.error(item.content || '解散组织失败');
        setDissolving(false);
        return;
      }

      if (item.type === 'complete') {
        setDissolveFinished(true);
        setDissolving(false);
        sessionStorage.removeItem(`org_${id}`);
        messageApi.success('组织已解散');
        completeTimerRef.current = window.setTimeout(() => {
          navigate('/organization');
        }, 3000);
        return;
      }

      if (!graph) return;

      setOrgStates(prev => applyDissolveStreamItemToStates(prev, graph, item));
    },
    [id, messageApi, navigate]
  );

  /** 执行解散组织。 */
  const handleDissolve = async () => {
    if (!id || !graphRef.current) return;

    setConfirmOpen(false);
    setDissolving(true);
    setDissolveFinished(false);
    setOrgStates(buildFullyLitOrgStates(graphRef.current));
    abortControllerRef.current = new AbortController();
    createQueue({
      onItem: handleQueueItem,
      minIntervalMs: pageConsumeIntervalMs,
    });

    try {
      const body = await dissolveOrganizationStream(
        id,
        abortControllerRef.current
      );
      const reader = body.getReader();
      const decoder = new TextDecoder();
      const cache = { current: '' };
      let done = false;

      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;

        if (!value) continue;

        const chunk = decoder.decode(value, { stream: true });
        const items = parseOrganizationStreamChunk(chunk, cache);
        items.forEach(item => {
          enqueue(item);
        });
      }

      if (cache.current.trim()) {
        parseOrganizationStreamChunk('', cache).forEach(item => {
          enqueue(item);
        });
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return;
      }
      messageApi.error((err as Error).message || '解散组织失败，请稍后重试');
      setDissolving(false);
    } finally {
      abortControllerRef.current = null;
    }
  };

  const orgName = detail?.organization.name || orgInfo?.name || '未知组织';
  const statusBadge = dissolveFinished
    ? {
        text: '组织已解散',
        icon: <CheckCircleFilled />,
        className: 'border-[#fee2e2] bg-[#fef2f2] text-[#dc2626]',
      }
    : dissolving
      ? {
          text: '组织解散中...',
          icon: <LoadingOutlined />,
          className: 'border-[#fde68a] bg-[#fffbeb] text-[#d97706]',
        }
      : {
          text: '组织构建成功',
          icon: <CheckCircleFilled />,
          className: 'border-[#d1fae5] bg-[#ecfdf5] text-[#059669]',
        };

  return (
    <div className="h-full min-h-0 overflow-auto px-10 py-6">
      {contextHolder}

      <div className="mx-auto flex h-full min-h-0 max-w-[1200px] flex-col gap-4">
        <div className="flex items-center justify-between rounded-2xl bg-white px-6 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={() => navigate('/organization')}
              className="flex cursor-pointer items-center justify-center border-none bg-transparent p-0 text-lg text-[#6b7280] transition-colors hover:text-[#111827]"
            >
              <LeftOutlined />
            </button>

            <div className="relative">
              <h2 className="relative top-[-6px] text-[22px] font-bold leading-none text-[#111827]">
                {orgName}
              </h2>
              <div className="absolute bottom-1 left-0 flex w-[300px] items-center gap-1 text-[11px] tracking-wide text-[#9ca3af]">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#22c55e]" />
                LIVE ORGANIZATION VIEW
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div
              className={`flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium ${statusBadge.className}`}
            >
              {statusBadge.icon}
              {statusBadge.text}
            </div>
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={dissolving}
              disabled={dissolveFinished || isLoading || !detail}
              onClick={() => setConfirmOpen(true)}
              className="!rounded-lg !border-none !bg-[#fef2f2] !px-4 !text-sm !font-medium !text-[#ef4444] !shadow-none hover:!bg-[#fee2e2] disabled:!bg-[#f3f4f6] disabled:!text-[#9ca3af]"
            >
              解散组织
            </Button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden rounded-2xl bg-white shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Spin size="large" />
            </div>
          ) : !detail ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-[#9ca3af]">
              <div className="text-4xl">🏗️</div>
              <p className="text-sm">
                组织详情获取失败，暂时无法渲染组织架构图
              </p>
            </div>
          ) : (
            <div className="h-full w-full">
              <OrganizationDiagramRenderer
                detail={detail}
                orgStates={orgStates}
                onCompiled={handleCompiled}
                mode="management"
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        footer={null}
        centered
        width={420}
        closable={false}
        styles={{ content: { borderRadius: 20, padding: '32px 28px 28px' } }}
      >
        <div className="text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#fef2f2]">
            <ExclamationCircleOutlined className="text-3xl text-[#ef4444]" />
          </div>

          <h3 className="mb-3 text-xl font-bold text-[#111827]">
            确认解散组织？
          </h3>
          <p className="mx-auto mb-7 max-w-[300px] text-sm leading-relaxed text-[#6b7280]">
            此操作将永久删除「
            <span className="font-medium text-[#111827]">{orgName}</span>
            」及其所有下属部门和人员数据。该操作不可撤销。
          </p>

          <div className="flex justify-center gap-4">
            <Button
              onClick={() => setConfirmOpen(false)}
              className="!h-11 !min-w-[120px] !rounded-xl !border-[#e5e7eb] !text-sm !font-medium"
            >
              取消
            </Button>
            <Button
              danger
              type="primary"
              loading={dissolving}
              onClick={handleDissolve}
              className="!h-11 !min-w-[120px] !rounded-xl !border-none !bg-[#ef4444] !text-sm !font-medium hover:!bg-[#dc2626]"
            >
              确认解散
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizationDetailPage;
